import React, { useMemo, useId } from 'react'
import { Tooltip } from '@theneatcompany/nui'
import { FormattedMessage } from 'react-intl'

import { useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import Form from '@/components/forms/Form'
import { Rows } from '@/components/layout'
import CheckoutSideChooseModules from '@/components/blocks/CheckoutSideChooseModules'
import CheckboxButton from '@/components/blocks/CheckboxButton'
import { Button, Text } from '@/components/common'

import SignUpLayout from './SignUpLayout'
import styles from './SignUp.module.scss'
import FormBox from './FormBox'

const SelectPlan = () => {
  const { coupon, plan, saveStepSelectPlan } = useSignUp()
  const planDifferencesId = useId()

  const saveData = async (model) => {
    const { vip = false, ai = false } = model

    const sku = `neat${ai ? 'ai' : ''}${vip ? 'vip' : ''}yr`

    await saveStepSelectPlan(sku)
  }

  const schema = Yup.object({
    neat: Yup.bool(),
    ai: Yup.bool(),
    vip: Yup.bool(),
  })

  const baseModulePrice = useMemo(() => {
    if (!Array.isArray(plan?.modules)) return 0
    return plan.modules.find(({ type }) => type === 'base')?.price || 0
  }, [plan?.modules])

  const planPrice = useMemo(() => {
    if (coupon?.discount?.amount && baseModulePrice) return baseModulePrice - coupon.discount.amount

    return baseModulePrice || 0
  }, [coupon, baseModulePrice])

  const strikePrice = useMemo(() => {
    if (baseModulePrice && coupon?.discount?.amount) return baseModulePrice

    return null
  }, [baseModulePrice, coupon])

  const handleScrollToPlan = () => {
    const el = document.getElementById(planDifferencesId)
    el?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <SignUpLayout
      sideContent={<CheckoutSideChooseModules planDifferencesId={planDifferencesId} />}
    >
      <FormBox withPricingBar={false}>
        <h1 className={styles.headline}>
          Verify your solution
        </h1>
        <p className="text-center" />
        <Form initialValues={{ neat: true, ai: plan?.sku.includes('ai'), vip: plan?.sku.includes('vip') }} onSubmit={saveData} schema={schema}>
          <Tooltip className={styles.tooltip} content={<FormattedMessage id="pages.signUp.form.selectPlan.baseTooltip" />} placement="top">
            <CheckboxButton checked disabled id="neat" name="Neat:" price={`$${planPrice}/yearly`} smallPrint="Base subscription (required)" strikePrice={strikePrice} />
          </Tooltip>

          <div className={styles.subsectionHeader}>
            <h3>Would you like additional solutions?</h3>
            <p>Choose all that apply</p>
          </div>
          <Rows gap={8}>
            <CheckboxButton data-testid="buy-vip-checkbox" id="vip" name="VIP Service:" price="+ $50/year" smallPrint="Requires a Neat subscription" />
            <CheckboxButton data-testid="buy-ai-checkbox" id="ai" name="Automated Insights:" price="+ $150/year" smallPrint="Requires a Neat subscription" />
          </Rows>
          <Form.Action
            className={styles.submit}
            data-testid="buy-solution-continue-cta"
            messageId="pages.signUp.form.selectPlan.nextButton"
            size="xlarge"
            submittingMessageId="cta.processing"
            type="submit"
          />
        </Form>
        <Rows center className={styles.scrollToPlan} gap={4}>
          <Text.P center noMargin size="small">What are the differences between the plans?</Text.P>
          <Button
            data-testid="scroll-to-plan-differences"
            iconOnly
            iconProps={{ name: 'chevron-down', title: 'See plan differences' }}
            onClick={handleScrollToPlan}
            variant="default"
          />
        </Rows>
      </FormBox>
    </SignUpLayout>
  )
}

export default SelectPlan
